import React, { useState, useRef, useCallback, useEffect } from "react";
import { Provider, connect } from "react-redux";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import Popup from "reactjs-popup";
import { Toggle } from "@opendate-app/setlist";
import "../../TeamManager/styles/teamManagerStyles.css";
import SettingsPageWrapper from "../../../shared/SettingsPageWrapper";
import deleteModalContentStyle from "../../../shared/deleteModalContentStyle";
import AgeRestrictionsSettings from '../../AgeRestrictionsSettings';
import EventTimeDefaults from "./EventTimeDefaults";
import TrashIcon from "../../shared/Icons/TrashIcon";
import XCloseIcon from "../../shared/Icons/XCloseIcon";
import PlusIcon from "../../shared/Icons/PlusIcon";

import configureStore from "../../ShowActivitySettings/store/ShowActivitySettingsStore";
import EventTemplatesManager from "../../ShowActivitySettings/components/EventTemplatesManager";
import * as actions from "../../ShowActivitySettings/actions/showActivitySettingsActionCreators";

const mapStateToProps = (state) => ({
  csrfToken: state.csrfToken,
  teamId: state.teamId,
  team: state.team,
  eventTemplates: state.eventTemplates,
  eventTemplateForForm: state.eventTemplateForForm,
  financesVisible: state.financesVisible,
  financeCategories: state.financeCategories,
  notesVisible: state.notesVisible,
  noteTemplates: state.noteTemplates,
});

const mapDispatchToProps = (dispatch) => ({
  eventTemplatesChanged: (eventTemplates) => dispatch(actions.eventTemplatesChanged(eventTemplates)),
  eventTemplateForFormChanged: (eventTemplateForForm) => dispatch(actions.eventTemplateForFormChanged(eventTemplateForForm)),
  createEventTemplate: (csrfToken, teamId, eventTemplate, eventTemplates) => dispatch(actions.createEventTemplate(dispatch, csrfToken, teamId, eventTemplate, eventTemplates)),
  updateEventTemplate: (csrfToken, teamId, eventTemplate, eventTemplates) => dispatch(actions.updateEventTemplate(dispatch, csrfToken, teamId, eventTemplate, eventTemplates)),
  deleteEventTemplate: (csrfToken, teamId, eventTemplate, eventTemplates) => dispatch(actions.deleteEventTemplate(dispatch, csrfToken, teamId, eventTemplate, eventTemplates)),
  financesVisibleChanged: (financesVisible) => dispatch(actions.financesVisibleChanged(financesVisible)),
  financeCategoriesChanged: (financeCategories) => dispatch(actions.financeCategoriesChanged(financeCategories)),
  notesVisibleChanged: (notesVisible) => dispatch(actions.notesVisibleChanged(notesVisible)),
  noteTemplatesChanged: (noteTemplates) => dispatch(actions.noteTemplatesChanged(noteTemplates)),
});

const ConnectedEventTemplatesManager = connect(mapStateToProps, mapDispatchToProps)(EventTemplatesManager);

const NewTemplateButton = connect(mapStateToProps, mapDispatchToProps)(
  ({ eventTemplates, eventTemplatesChanged, eventTemplateForFormChanged }) => (
    <button
      type="button"
      className="team-btn-secondary flex-shrink-0"
      onClick={(e) => {
        e.preventDefault();
        const newEventTemplate = {
          id: `new-${eventTemplates.length}`,
          name: '',
          description: '',
          notes: [],
          income_template: { is_income: true, finance_line_items: [] },
          expense_template: { is_income: false, finance_line_items: [] },
        };
        eventTemplateForFormChanged(newEventTemplate);
        eventTemplatesChanged([...eventTemplates, newEventTemplate]);
      }}
    >
      <PlusIcon fill="currentColor" width="16" height="16" />
      <span>New Template</span>
    </button>
  )
);

const ShowDetails = ({ csrfToken, teamId, initialSettings }) => {
  const [settings, setSettings] = useState(initialSettings);
  const debounceRef = useRef(null);

  const saveSettings = useCallback((updatedSettings) => {
    client.patch(`/teams/${teamId}`, {
      team: {
        show_activity_settings_attributes: updatedSettings.filter(s => s.name.trim() !== "")
      }
    })
    .then((response) => {
      setSettings(prev =>
        prev
          .filter(s => s.name.trim() !== "" && s._destroy === undefined)
          .map(s => {
            let match = response.data.find(item => item.name === s.name);
            return match !== undefined ? { ...s, ...match } : s;
          })
      );
      toastSuccess("Show details saved");
    })
    .catch((error) => {
      if (Array.isArray(error.response?.data)) {
        setSettings(prev => prev.map(s => {
          let match = error.response.data.find(e => e.id === s.id && e.name === s.name);
          return match !== undefined ? { ...s, errors: match.errors } : s;
        }));
      }
      toastError("An error occurred");
    });
  }, [csrfToken, teamId]);

  const debouncedSave = useCallback((updatedSettings) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveSettings(updatedSettings), 500);
  }, [saveSettings]);

  const immediateSave = useCallback((updatedSettings) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    saveSettings(updatedSettings);
  }, [saveSettings]);

  const settingNameChanged = (index, value) => {
    const updated = settings.map((s, i) => i === index ? { ...s, name: value } : s);
    setSettings(updated);
    debouncedSave(updated);
  };

  const settingDefaultChanged = (index, value) => {
    const updated = settings.map((s, i) => i === index ? { ...s, default: value } : s);
    setSettings(updated);
    immediateSave(updated);
  };

  const addSetting = () => {
    setSettings(settings.concat({ id: null, name: "", default: false }));
  };

  const deleteSetting = (index) => {
    const updated = settings.map((s, i) => i === index ? { ...s, _destroy: true } : s);
    setSettings(updated);
    immediateSave(updated);
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div>
        <p className="mb-1 text-dark" style={{ fontSize: "16px" }}>
          <strong>Show Details</strong>
        </p>
        <p className="m-0 small text-muted">
          Customize which fields show up by default in the Show Details section for each event.
        </p>
      </div>
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {settings.map((setting, index) => {
          if (setting._destroy !== undefined) { return null; }

          let errors;
          if (setting.errors && setting.errors.length > 0) {
            errors = (
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {setting.errors.map((error, errorIndex) => (
                  <li key={`error-${errorIndex}`} className="text-danger font-weight-bold" style={{ fontSize: 13 }}>{error}</li>
                ))}
              </ul>
            );
          }

          return (
            <React.Fragment key={index}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid var(--sl-border-primary)',
                  borderRadius: 8,
                  padding: '10px 16px',
                  backgroundColor: 'var(--sl-bg-primary)',
                }}
              >
                <input
                  type="text"
                  value={setting.name}
                  onChange={(e) => settingNameChanged(index, e.target.value)}
                  placeholder="Field name"
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: 14,
                    color: 'var(--sl-text-primary)',
                    fontFamily: "'Open Sans', sans-serif",
                  }}
                />
                <input
                  type="checkbox"
                  className="team-checkbox"
                  checked={setting.default}
                  onChange={(e) => settingDefaultChanged(index, e.target.checked)}
                  id={`default-check-${index}`}
                  style={{ margin: '0 12px 0 8px' }}
                />
                <Popup
                  trigger={
                    <a
                      href="#"
                      aria-label="Delete show detail"
                      style={{ color: 'var(--sl-text-placeholder)', fontSize: 16, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}
                      onClick={(e) => e.preventDefault()}
                    >
                      <TrashIcon fill="currentColor" width="16" height="16" />
                    </a>
                  }
                  onOpen={(e) => e.preventDefault()}
                  modal
                  contentStyle={deleteModalContentStyle}
                  closeOnDocumentClick
                >
                  {close => (
                    <div>
                      <div className="row">
                        <div className="col">
                          <div className="row">
                            <div className="col">
                              <h5 style={{ marginBottom: "16px" }}>
                                <strong>Delete field?</strong>
                              </h5>
                            </div>
                            <div className="col-xs-auto">
                              <a
                                href="#"
                                aria-label="Close"
                                className="text-dark"
                                style={{ paddingRight: "15px", display: 'inline-flex', alignItems: 'center' }}
                                onClick={(e) => { e.preventDefault(); close(); }}
                              >
                                <XCloseIcon fill="currentColor" width="16" height="16" />
                              </a>
                            </div>
                          </div>
                          <p>You're about to delete this field.</p>
                          <p>This cannot be undone.</p>
                          <p>Are you sure?</p>
                          <div className="text-right">
                            <button type="button" className="team-btn-secondary" onClick={(e) => { e.preventDefault(); close(); }}>
                              <span>Cancel</span>
                            </button>
                            <button
                              type="button"
                              className="team-btn-danger ml-3"
                              onClick={(e) => { e.preventDefault(); close(); deleteSetting(index); }}
                            >
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Popup>
              </div>
              {errors}
            </React.Fragment>
          );
        })}
        <div>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); addSetting(); }}
            className="team-btn-secondary"
          >
            <PlusIcon fill="currentColor" width="16" height="16" />
            <span>Add Show Detail</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const PromoterVisibility = ({ csrfToken, teamId, initialValue }) => {
  const [checked, setChecked] = useState(initialValue || false);

  const handleToggle = (e) => {
    const newValue = e.target.checked;
    setChecked(newValue);

    client.patch(`/teams/${teamId}`, {
      team: { promoter_visibility: newValue }
    })
    .then(() => {
      toastSuccess("Promoter visibility updated");
    })
    .catch(() => {
      setChecked(!newValue);
      toastError("An error occurred");
    });
  };

  return (
    <div>
      <p className="mb-1 text-dark" style={{ fontSize: "16px" }}>
        <strong>Promoter Visibility</strong>
      </p>
      <p className="m-0 small text-muted">
        When on, users with the Promoter role can see artist names. When off, they only see that an event exists—artist names are hidden.
      </p>
      <div className="mt-3">
        <Toggle
          id="team-promoter-visibility"
          label="Show artist names to Promoters"
          checked={checked}
          onChange={handleToggle}
        />
      </div>
    </div>
  );
};

export const CalendarVenueName = ({ teamId, initialValue }) => {
  const [checked, setChecked] = useState(initialValue ?? false);

  const handleToggle = (e) => {
    const newValue = e.target.checked;
    setChecked(newValue);

    client.patch(`/teams/${teamId}`, {
      team: { calendar_venue_name_enabled: newValue }
    })
    .then(() => {
      toastSuccess("Calendar venue name visibility updated");
    })
    .catch(() => {
      setChecked(!newValue);
      toastError("An error occurred");
    });
  };

  return (
    <div>
      <p className="mb-1 text-dark" style={{ fontSize: "16px" }}>
        <strong>Venue Name on Calendar</strong>
      </p>
      <p className="m-0 small text-muted">
        When on, calendar event pills show the venue name alongside the event time.
      </p>
      <div className="mt-3">
        <Toggle
          id="team-calendar-venue-name-enabled"
          label="Show venue name on calendar"
          checked={checked}
          onChange={handleToggle}
        />
      </div>
    </div>
  );
};

export const CalendarTypedDateEntry = ({ teamId, initialValue }) => {
  const [checked, setChecked] = useState(initialValue ?? false);

  const handleToggle = (e) => {
    const newValue = e.target.checked;
    setChecked(newValue);

    client.patch(`/teams/${teamId}`, {
      team: { calendar_typed_date_entry_enabled: newValue }
    })
    .then(() => {
      toastSuccess("Typed date entry updated");
    })
    .catch(() => {
      setChecked(!newValue);
      toastError("An error occurred");
    });
  };

  return (
    <div>
      <p className="mb-1 text-dark" style={{ fontSize: "16px" }}>
        <strong>Typed Date &amp; Time Entry</strong>
      </p>
      <p className="m-0 small text-muted">
        When on, the start, doors, and end fields accept typed entries such as "10/3/26 8pm" when you confirm an event, in addition to the picker.
      </p>
      <div className="mt-3">
        <Toggle
          id="team-calendar-typed-date-entry-enabled"
          label="Allow typing dates and times when confirming events"
          checked={checked}
          onChange={handleToggle}
        />
      </div>
    </div>
  );
};

export const CalendarPopupForecast = ({ teamId, initialValue }) => {
  const [checked, setChecked] = useState(initialValue ?? false);

  const handleToggle = (e) => {
    const newValue = e.target.checked;
    setChecked(newValue);

    client.patch(`/teams/${teamId}`, {
      team: { calendar_popup_forecast_enabled: newValue }
    })
    .then(() => {
      toastSuccess("Calendar popup forecast visibility updated");
    })
    .catch(() => {
      setChecked(!newValue);
      toastError("An error occurred");
    });
  };

  return (
    <div>
      <p className="mb-1 text-dark" style={{ fontSize: "16px" }}>
        <strong>Forecast &amp; Revenue on Calendar Popups</strong>
      </p>
      <p className="m-0 small text-muted">
        When on, calendar event popups show forecasted tickets and actual revenue alongside tickets sold and gross. Actual revenue only shows to people who can view the event's P&amp;L.
      </p>
      <div className="mt-3">
        <Toggle
          id="team-calendar-popup-forecast-enabled"
          label="Show forecast and revenue on calendar popups"
          checked={checked}
          onChange={handleToggle}
        />
      </div>
    </div>
  );
};

const SettingsEventsDetails = ({ settingsPath, lastEditedAt, changelogVersions, promoterVisibility, calendarVenueNameEnabled, calendarTypedDateEntryEnabled, calendarPopupForecastEnabled, showActivityDefaults, eventTimeDefaults = {}, defaultUnits, defaultMinutes, defaultHours, showActivitySettings, ageRestrictionSettings = [], ageRestrictionVenueOwnerships = [], venueAgeRestrictionsByVenueOwnership = {}, ...storeProps }) => {
  // Page is client-rendered (prerender: false), so the #event-templates node
  // doesn't exist when the browser first honors the hash — scroll it in once mounted.
  useEffect(() => {
    if (window.location.hash === "#event-templates") {
      document.getElementById("event-templates")?.scrollIntoView();
    }
  }, []);

  return (
    <SettingsPageWrapper settingsPath={settingsPath} title="Events" lastEditedAt={lastEditedAt} changelogVersions={changelogVersions}>
      <div className="team-member-card">
        <div className="team-member-card-header">
          <div className="team-member-card-header-left">
            <div className="team-member-card-title-row">
              <h2 className="team-member-card-title">Events</h2>
            </div>
            <p className="team-member-card-subtitle">
              Customize your event settings.
            </p>
          </div>
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
        <div style={{ padding: '25px 20px' }}>
          <EventTimeDefaults
            teamId={storeProps.teamId}
            initialOffsets={showActivityDefaults}
            eventTimeDefaults={eventTimeDefaults}
            defaultUnits={defaultUnits}
            defaultMinutes={defaultMinutes}
            defaultHours={defaultHours}
          />
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
        <div style={{ padding: '25px 20px' }}>
          <PromoterVisibility
            csrfToken={storeProps.csrfToken}
            teamId={storeProps.teamId}
            initialValue={promoterVisibility}
          />
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
        <div style={{ padding: '25px 20px' }}>
          <CalendarVenueName
            teamId={storeProps.teamId}
            initialValue={calendarVenueNameEnabled}
          />
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
        <div style={{ padding: '25px 20px' }}>
          <CalendarTypedDateEntry
            teamId={storeProps.teamId}
            initialValue={calendarTypedDateEntryEnabled}
          />
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
        <div style={{ padding: '25px 20px' }}>
          <CalendarPopupForecast
            teamId={storeProps.teamId}
            initialValue={calendarPopupForecastEnabled}
          />
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
        <div style={{ padding: '25px 20px' }}>
          <ShowDetails
            csrfToken={storeProps.csrfToken}
            teamId={storeProps.teamId}
            initialSettings={showActivitySettings}
          />
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
        <div id="event-templates" style={{ padding: '25px 20px', scrollMarginTop: '20px' }}>
          <Provider store={configureStore(storeProps)}>
            {!storeProps.team.package.offer_v3 ? (
              <div className="d-flex align-items-start justify-content-between" style={{ marginBottom: 16 }}>
                <div>
                  <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
                    <strong>Event Templates</strong>
                  </p>
                  <p className="m-0 small text-muted">
                    Create event templates to streamline the creation process.
                  </p>
                </div>
                <NewTemplateButton />
              </div>
            ) : null}
            <div className="settings-event-templates">
              <ConnectedEventTemplatesManager />
            </div>
          </Provider>
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />
        <div style={{ padding: '25px 20px' }}>
          <AgeRestrictionsSettings
            teamId={storeProps.team.id}
            csrfToken={storeProps.csrfToken}
            initialSettings={ageRestrictionSettings}
            venueOwnerships={ageRestrictionVenueOwnerships}
            initialVenueAgeRestrictions={venueAgeRestrictionsByVenueOwnership}
          />
        </div>
      </div>
    </SettingsPageWrapper>
  );
};

export default SettingsEventsDetails;
